import React, { useState, useEffect } from 'react';
import {
	RadioSetting,
	RadioSettingOption,
} from '../../components/SettingCards/RadioSetting/RadioSetting';
import { SecondaryButton } from '../../components/Buttons/Buttons';
import { GENERAL_SETTING_IDS } from '../../config/SettingIds';
import {
	getSettingConfig,
	FileDownloadSettingConfig,
} from '../../config/SettingsDefinitions';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants } from '../../utils/SettingsLogger';
import { AotCategory } from '../../utils/SettingsLogger';
import './FileDownloadLocationSetting.css';

export interface FileDownloadLocationSettingProps {
	disabled?: boolean;
}

export interface FileDownloadLocationSettingValue {
	option: string;
	downloadLocation: string;
}

export interface FileDownloadLocationChangeSetting {
	changeLocation: boolean;
	downloadLocation?: string;
}

export const FileDownloadLocationSetting: React.FC<FileDownloadLocationSettingProps> = ({
	disabled = false,
}) => {
	// State for radio settings
	const [downloadLocationOption, setDownloadLocationOption] = useState('same-location');
	const [downloadPath, setDownloadPath] = useState('');

	// Load setting values on component mount
	useEffect(() => {
		const loadSetting = async () => {
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();
				const response = await bridge.getSetting({
					settingKey: GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION,
				});

				if (response.status === 0 && response.data) {
					const settingValue = response.data.settingValue;
					try {
						const value =
							typeof settingValue === 'string' ? JSON.parse(settingValue) : settingValue;

						if (value.option) {
							setDownloadLocationOption(value.option);
						}
						if (value.downloadLocation) {
							setDownloadPath(value.downloadLocation);
						}
					} catch (parseError) {
						// If parsing fails, treat as simple string value
						setDownloadLocationOption(settingValue as string);
					}
				}
			} catch (error) {
				const errorMessage = `Failed to load file download location setting: ${error}`;
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					errorMessage,
					AotCategory.Error,
					errorMessage
				);
			}
		};

		loadSetting();
	}, []);

	// Handle change button click - call setSetting and expect path response
	const handleChangeDownloadLocation = async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const settingValue: FileDownloadLocationSettingValue = {
				option: downloadLocationOption,
				downloadLocation: downloadPath,
			};

			const response = await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.CHANGE_FILE_DOWNLOAD_LOCATION,
				settingValue: JSON.stringify(settingValue),
			});

			// Update path from response only
			if (response?.data?.settingValue) {
				const responseSettingValue = response.data.settingValue;
				try {
					const responseValue =
						typeof responseSettingValue === 'string'
							? JSON.parse(responseSettingValue)
							: responseSettingValue;

					if (responseValue.downloadLocation) {
						setDownloadPath(responseValue.downloadLocation);

						SettingsLogger.getInstance().write(
							logConstants.INFO,
							'File download location updated successfully',
							AotCategory.Info,
							'File download location updated successfully'
						);
					}
				} catch (parseError) {
					// If parsing fails, treat as simple string path
					setDownloadPath(responseSettingValue as string);
				}
			}
		} catch (error) {
			const errorMessage = `Failed to browse for download location: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
		}
	};

	// Handle radio option change - call setSetting for second option only
	const handleRadioOptionChange = async (newOption: string) => {
		setDownloadLocationOption(newOption);

		// Determine the path value to use
		const pathToUse = newOption === 'ask-every-time' ? '' : downloadPath;

		try {
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Setting new option: ${newOption} with path: ${pathToUse}`
			);
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const settingValue: FileDownloadLocationSettingValue = {
				option: newOption,
				downloadLocation: pathToUse,
			};
			await bridge.setSetting({
				settingKey: GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION,
				settingValue: JSON.stringify(settingValue),
			});

			SettingsLogger.getInstance().write(
				logConstants.INFO,
				'File download option updated to ask every time',
				AotCategory.Info,
				'File download option updated to ask every time'
			);
		} catch (error) {
			const errorMessage = `Failed to update file download option: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
		}
	};

	const fileDownloadLocationSettingConfig = getSettingConfig(
		GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION
	) as FileDownloadSettingConfig;

	return (
		<RadioSetting
			settingId={GENERAL_SETTING_IDS.FILE_DOWNLOAD_LOCATION}
			title={fileDownloadLocationSettingConfig?.title}
			description={fileDownloadLocationSettingConfig?.description}
			disabled={disabled}
			options={fileDownloadLocationSettingConfig.options.map(
				(option, index) =>
					({
						...option,
						content:
							index === 0 ? (
								<div className="download-path">
									<input
										type="text"
										value={downloadPath}
										readOnly
										className="path-input"
										placeholder={
											fileDownloadLocationSettingConfig.filePickerConfig?.placeholder
										}
										disabled={true}
									/>
									<SecondaryButton
										onClick={handleChangeDownloadLocation}
										className="change-button"
										disabled={downloadLocationOption !== option.value || disabled}
									>
										{fileDownloadLocationSettingConfig.filePickerConfig?.buttonLabel}
									</SecondaryButton>
								</div>
							) : undefined,
					}) as RadioSettingOption
			)}
			selectedValue={downloadLocationOption}
			onChange={handleRadioOptionChange}
		/>
	);
};
